import React, { useCallback, useEffect, useState } from "react";
import { Card, Group, Title, SegmentedControl, Box } from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import OrgLevelTypesCard from "./OrgLevelTypesCard";
import OperatingLevelTypesCard from "./OperatingLevelTypesCard";

const buildOperatingLevelHierarchy = types => {
  if (!Array.isArray(types) || types.length === 0) return null;

  const byId = new Map();
  types.forEach(t => {
    byId.set(t.id, { ...t, sub_types: [] });
  });

  const roots = [];
  types.forEach(t => {
    const node = byId.get(t.id);
    const parentId = t.operation_type_parent_id;
    if (parentId != null && byId.has(parentId)) {
      byId.get(parentId).sub_types.push(node);
    } else {
      roots.push(node);
    }
  });

  if (roots.length === 1) return roots[0];
  return { name: "Operations", sub_types: roots, synthetic_root: true };
};

const CompanyStructureCard = () => {
  const { getVerifiedJWT } = useAuth();
  const [view, setView] = useState("sales");
  const [orgLevelTypes, setOrgLevelTypes] = useState([]);
  const [typesHierarchy, setTypesHierarchy] = useState(null);
  const [operatingLevelTypes, setOperatingLevelTypes] = useState([]);
  const [operatingTypesHierarchy, setOperatingTypesHierarchy] = useState(null);

  const fetchOrgLevelTypes = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint("/v1/org-level/types"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        setOrgLevelTypes(result.data?.types_list || []);
        setTypesHierarchy(result.data?.types_hierarchy || null);
      }
    } catch (err) {}
  }, [getVerifiedJWT]);

  const fetchOperatingLevelTypes = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;
      const response = await fetch(getApiEndpoint("/v1/operations/types"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        const list = result.data?.types_list || [];
        setOperatingLevelTypes(list);
        setOperatingTypesHierarchy(
          result.data?.types_hierarchy || buildOperatingLevelHierarchy(list)
        );
      }
    } catch (err) {}
  }, [getVerifiedJWT]);

  useEffect(() => {
    fetchOrgLevelTypes();
    fetchOperatingLevelTypes();
  }, [fetchOrgLevelTypes, fetchOperatingLevelTypes]);

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={3}>Company Structure</Title>
        <SegmentedControl
          value={view}
          onChange={setView}
          data={[
            { label: "Sales Groups", value: "sales" },
            { label: "Operations", value: "operations" },
          ]}
        />
      </Group>

      <Box>
        {view === "sales" ? (
          <OrgLevelTypesCard
            embedded
            orgLevelTypes={orgLevelTypes}
            typesHierarchy={typesHierarchy}
            onRefresh={fetchOrgLevelTypes}
          />
        ) : (
          <OperatingLevelTypesCard
            embedded
            operatingLevelTypes={operatingLevelTypes}
            typesHierarchy={operatingTypesHierarchy}
            onRefresh={fetchOperatingLevelTypes}
          />
        )}
      </Box>
    </Card>
  );
};

export default CompanyStructureCard;
